/**
 * Studio installation: devices, ports and links
 * (docs/wiring-documentation-proposal.md, "Model details").
 *
 * The shapes here mirror the Zero rows (camelCase), so the same helpers run in
 * the mutators (client and server), the frontend and tests. They are pure:
 * callers load the rows and apply the returned plans.
 */
import { ConflictError } from "../errors.js";
export const PORT_DIRECTIONS = ["input", "output", "bidirectional"];
export const PORT_SIGNALS = [
    "sdi",
    "hdmi",
    "fiber",
    "analog_audio",
    "aes",
    "madi",
    "ethernet",
    "genlock",
    "other",
];
export const PORT_SIGNAL_LABELS = {
    sdi: "SDI",
    hdmi: "HDMI",
    fiber: "Fibre",
    analog_audio: "Analog audio",
    aes: "AES",
    madi: "MADI",
    ethernet: "Ethernet",
    genlock: "Genlock",
    other: "Other",
};
export const LINK_VERIFIED_BY = ["manual", "probe", "device_report"];
export const PATCH_STATUSES = [
    "pending_setup",
    "patched",
    "pending_restore",
    "restored",
];
/** Short port label: "DL7", "In 21", "Aux 1" (name wins over number). */
export function portLabel(port) {
    if (port.name && port.name.trim())
        return port.name;
    const prefix = port.group && port.group.trim()
        ? port.group
        : port.direction === "input"
            ? "In"
            : port.direction === "output"
                ? "Out"
                : "I/O";
    return `${prefix} ${port.number}`;
}
/** The port's 1-based number with its group or direction: "In 21", "Aux 2". */
export function portNumberLabel(port) {
    const prefix = port.group && port.group.trim()
        ? port.group
        : port.direction === "input"
            ? "In"
            : port.direction === "output"
                ? "Out"
                : "I/O";
    return `${prefix} ${port.number}`;
}
/** Port label with its device and number: "Videohub · In 21 (IO OUT 1)". */
export function describePort(port, devices) {
    const device = devices?.find((d) => d.id === port.deviceID);
    const numbered = portNumberLabel(port);
    const named = port.name && port.name.trim() && port.name !== numbered
        ? `${numbered} "${port.name}"`
        : numbered;
    return device ? `${device.name} ${named}` : named;
}
// ─── Link invariant ─────────────────────────────────────────
/**
 * Check a new or changed link against the wiring of its studio.
 *
 * - `from` must be an output or bidirectional port, `to` an input or
 *   bidirectional port, and both must belong to the link's studio.
 * - A port of temporary gear (a device with a config) is only linked by
 *   links of that config.
 * - Each port is in at most one link per scope (permanent, or per config),
 *   counting both ends: a bidirectional port that is `to` in one link cannot
 *   be `from` in another. The partial unique indexes only cover one end each,
 *   and client-side optimistic writes enforce nothing, so this check is the
 *   real guard.
 *
 * Returns an error message, or null when the link is valid.
 */
export function validateLink(link, ports, links, devices) {
    if (link.fromPortID === link.toPortID) {
        return "A link needs two different ports";
    }
    const from = ports.find((port) => port.id === link.fromPortID);
    const to = ports.find((port) => port.id === link.toPortID);
    if (!from)
        return "Source port not found";
    if (!to)
        return "Destination port not found";
    if (from.studioID !== link.studioID || to.studioID !== link.studioID) {
        return "Both ports must belong to the link's studio";
    }
    if (from.direction === "input") {
        return `${describePort(from, devices)} is an input and cannot be a link source`;
    }
    if (to.direction === "output") {
        return `${describePort(to, devices)} is an output and cannot be a link destination`;
    }
    const scope = link.configID ?? null;
    // Temporary gear is cabled only by its own config's links: a permanent
    // link to it would claim the gear is always there
    for (const port of [from, to]) {
        const device = devices?.find((d) => d.id === port.deviceID);
        if (device?.configID && device.configID !== scope) {
            return scope
                ? `${device.name} is temporary gear of another config`
                : `${device.name} is temporary gear of a config; cable it in that config`;
        }
    }
    for (const other of links) {
        if (other.id === link.id)
            continue;
        if ((other.configID ?? null) !== scope)
            continue;
        for (const port of [from, to]) {
            if (other.fromPortID === port.id || other.toPortID === port.id) {
                const peerID = other.fromPortID === port.id ? other.toPortID : other.fromPortID;
                const peer = ports.find((p) => p.id === peerID);
                return `${describePort(port, devices)} is already linked${peer ? ` to ${describePort(peer, devices)}` : ""}`;
            }
        }
    }
    return null;
}
/** Throwing variant of {@link validateLink} for mutators (409 Conflict). */
export function assertValidLink(...args) {
    const error = validateLink(...args);
    if (error)
        throw new ConflictError(error);
}
// ─── Device tree ────────────────────────────────────────────
/** The device and all its descendants (children, grandchildren, ...). */
export function deviceSubtree(deviceID, devices) {
    const result = [];
    const seen = new Set();
    const queue = [deviceID];
    while (queue.length > 0) {
        const id = queue.shift();
        if (seen.has(id))
            continue;
        seen.add(id);
        result.push(id);
        for (const device of devices) {
            if (device.parentID === id && !seen.has(device.id)) {
                queue.push(device.id);
            }
        }
    }
    return result;
}
/**
 * Check a parent assignment: the parent must exist in the same studio and
 * must not be the device itself or one of its descendants.
 */
export function validateDeviceParent(device, parentID, devices) {
    if (!parentID)
        return null;
    if (parentID === device.id)
        return "A device cannot be its own parent";
    const parent = devices.find((d) => d.id === parentID);
    if (!parent)
        return "Parent device not found";
    if (parent.studioID !== device.studioID) {
        return "The parent device must be in the same studio";
    }
    if (deviceSubtree(device.id, devices).includes(parentID)) {
        return "A device cannot be placed inside one of its own children";
    }
    return null;
}
/**
 * Deleting a device deletes its child devices (cards and software inside a
 * PC), all their ports and every link touching one of those ports.
 */
export function planDeviceDelete(deviceID, devices, ports, links) {
    const deviceIDs = deviceSubtree(deviceID, devices);
    const deviceSet = new Set(deviceIDs);
    const portIDs = ports
        .filter((port) => deviceSet.has(port.deviceID))
        .map((port) => port.id);
    const portSet = new Set(portIDs);
    return {
        deviceIDs,
        portIDs,
        links: links.filter((link) => portSet.has(link.fromPortID) || portSet.has(link.toPortID)),
    };
}
/**
 * Moving a device to another studio moves its children and ports with it and
 * deletes its links to the devices staying behind. Links inside the moved
 * subtree (a card cabled to another card of the same PC) stay intact.
 */
export function planDeviceMove(deviceID, devices, ports, links) {
    const { deviceIDs, portIDs, links: touching, } = planDeviceDelete(deviceID, devices, ports, links);
    const portSet = new Set(portIDs);
    const linksToMove = touching.filter((link) => portSet.has(link.fromPortID) && portSet.has(link.toPortID));
    const linksToDelete = touching.filter((link) => !linksToMove.includes(link));
    return { deviceIDs, portIDs, linksToDelete, linksToMove };
}
/** Devices ordered as a tree: roots by name, children by position then name. */
export function sortDeviceTree(devices) {
    const ids = new Set(devices.map((d) => d.id));
    const byParent = new Map();
    for (const device of devices) {
        const parent = device.parentID && ids.has(device.parentID) ? device.parentID : null;
        const list = byParent.get(parent) ?? [];
        list.push(device);
        byParent.set(parent, list);
    }
    const compare = (a, b) => {
        const pa = a.position ?? Number.MAX_SAFE_INTEGER;
        const pb = b.position ?? Number.MAX_SAFE_INTEGER;
        if (pa !== pb)
            return pa - pb;
        return a.name.localeCompare(b.name);
    };
    const result = [];
    const seen = new Set();
    const visit = (parent, depth) => {
        const children = [...(byParent.get(parent) ?? [])].sort(parent === null ? (a, b) => a.name.localeCompare(b.name) : compare);
        for (const child of children) {
            if (seen.has(child.id))
                continue;
            seen.add(child.id);
            result.push({ device: child, depth });
            visit(child.id, depth + 1);
        }
    };
    visit(null, 0);
    // Devices caught in a parent cycle (should not exist) still show up
    for (const device of devices) {
        if (!seen.has(device.id))
            result.push({ device, depth: 0 });
    }
    return result;
}
/**
 * A UUID derived from `parts` (RFC 9562 version 8, "custom"): the same parts
 * give the same ID on the client and the server, so optimistic rows of a
 * mutator (template ports, audit rows) match the rows the server writes.
 * Not a cryptographic hash; the IDs are not secrets.
 */
export function deterministicUUID(...parts) {
    const input = parts.join("\u0000");
    // cyrb128: four 32-bit lanes
    let h1 = 1779033703;
    let h2 = 3144134277;
    let h3 = 1013904242;
    let h4 = 2773480762;
    for (let i = 0; i < input.length; i++) {
        const k = input.charCodeAt(i);
        h1 = h2 ^ Math.imul(h1 ^ k, 597399067);
        h2 = h3 ^ Math.imul(h2 ^ k, 2869860233);
        h3 = h4 ^ Math.imul(h3 ^ k, 951274213);
        h4 = h1 ^ Math.imul(h4 ^ k, 2716044179);
    }
    h1 = Math.imul(h3 ^ (h1 >>> 18), 597399067);
    h2 = Math.imul(h4 ^ (h2 >>> 22), 2869860233);
    h3 = Math.imul(h1 ^ (h3 >>> 17), 951274213);
    h4 = Math.imul(h2 ^ (h4 >>> 19), 2716044179);
    h1 ^= h2 ^ h3 ^ h4;
    h2 ^= h1;
    h3 ^= h1;
    h4 ^= h1;
    const hex = [h1, h2, h3, h4]
        .map((h) => (h >>> 0).toString(16).padStart(8, "0"))
        .join("");
    const variant = ((parseInt(hex[16], 16) & 0x3) | 0x8).toString(16);
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-8${hex.slice(13, 16)}-${variant}${hex.slice(17, 20)}-${hex.slice(20, 32)}`;
}
